/**
 * Shared creative primitives used by Creations, Decisions, Studio, chat approval cards and the
 * creative detail modal. Approval/rejection is human-only on the server (`creatives.approve/reject`).
 */
import { Check, CircleAlert, Film, Image as ImageIcon, Maximize2, ShieldCheck, X } from "lucide-react";
import { useState } from "react";
import { REJECTION_LABEL, REJECTION_REASONS, type Creative, type CreativeAsset, type RejectionReason } from "@/api/types";
import { useToolMutation } from "@/api/hooks";
import { cn } from "@/lib/cn";
import { humanize } from "@/lib/format";
import { useAuth } from "@/state/auth";
import { Pill, type Tone } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Checkbox, Chip, Textarea } from "../ui/Form";
import { Lightbox } from "../ui/Lightbox";
import { AssetPicker, type PickedAsset } from "../assets/AssetPicker";
import { safeMediaHref } from "@/lib/safe-url";

const STATUS: Record<string, { tone: Tone; label: string }> = {
  draft: { tone: "muted", label: "Draft" },
  generating: { tone: "info", label: "Generating" },
  qa_failed: { tone: "danger", label: "QA failed" },
  pending_approval: { tone: "amber", label: "Needs approval" },
  approved: { tone: "primary", label: "Approved" },
  rejected: { tone: "danger", label: "Rejected" },
  published: { tone: "neutral", label: "Published · paused" },
  live: { tone: "success", label: "Live" },
  killed: { tone: "danger", label: "Killed" },
  graveyard: { tone: "warning", label: "Graveyard" },
  dead: { tone: "muted", label: "Retired" },
};

export function creativeStatusMeta(status: string | null | undefined): { tone: Tone; label: string } {
  return STATUS[status ?? ""] ?? { tone: "muted", label: humanize(status ?? "unknown") };
}

export function CreativeStatusPill({ status, className, size }: { status: string | null | undefined; className?: string; size?: "xs" | "sm" }) {
  const m = creativeStatusMeta(status);
  return (
    <Pill tone={m.tone} dot className={className} size={size}>
      {m.label}
    </Pill>
  );
}

const VERDICT: Record<string, { tone: Tone; label: string }> = {
  insufficient_data: { tone: "muted", label: "Not enough data" },
  watch: { tone: "info", label: "Watch" },
  keep: { tone: "success", label: "Keep" },
  scale: { tone: "primary", label: "Scale" },
  iterate: { tone: "amber", label: "Iterate" },
  kill: { tone: "danger", label: "Kill" },
  graveyard: { tone: "warning", label: "Graveyard" },
  revive: { tone: "success", label: "Revive" },
  retire: { tone: "muted", label: "Retire" },
};

export function VerdictPill({ verdict, className }: { verdict: string | null | undefined; className?: string }) {
  if (!verdict) return null;
  const m = VERDICT[verdict] ?? { tone: "neutral" as Tone, label: humanize(verdict) };
  return (
    <Pill tone={m.tone} variant="outline" className={className}>
      {m.label}
    </Pill>
  );
}

/** Image or video with a graceful placeholder. */
export function CreativeMedia({
  url,
  mediaType,
  poster,
  className,
  fit = "cover",
  controls,
  alt = "",
}: {
  url?: string | null;
  mediaType?: "image" | "video" | string | null;
  poster?: string | null;
  className?: string;
  fit?: "cover" | "contain";
  controls?: boolean;
  alt?: string;
}) {
  // A new signed URL is a new load attempt; stale errors must not poison it.
  return <CreativeMediaLoad key={`${url ?? ""}|${poster ?? ""}`} {...{url, mediaType, poster, className, fit, controls, alt}} />;
}

function CreativeMediaLoad({url, mediaType, poster, className, fit, controls, alt}: Parameters<typeof CreativeMedia>[0]) {
  const [failed, setFailed] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const candidate = safeMediaHref(url);
  const video = mediaType === "video" || (mediaType !== "image" && /\.(mp4|mov|webm)(\?|$)/i.test(candidate ?? ""));
  const imageOnly = video && /\.(png|jpe?g|gif|webp)(\?|$)/i.test(candidate ?? "");
  const src = imageOnly || (video && candidate === safeMediaHref(poster)) ? undefined : candidate;
  const posterSrc = posterFailed ? undefined : safeMediaHref(poster) ?? (imageOnly ? candidate : undefined);
  const objectFit = fit === "contain" ? "object-contain" : "object-cover";
  if (video && (!src || failed) && posterSrc) return <div className={cn("relative overflow-hidden bg-black", className)}><img src={posterSrc} alt={alt} onError={() => setPosterFailed(true)} className={cn("size-full", objectFit)} /><span role="status" className="absolute inset-x-0 bottom-0 bg-black/75 p-2 text-center text-xs text-white">Video unavailable — showing poster only</span></div>;
  if ((!src || failed) && !posterSrc) return <div role="status" className={cn("grid place-items-center bg-[#151517] p-3 text-center text-xs text-muted", className)}>{video ? "Video unavailable" : failed ? "Preview unavailable — open the creation to inspect this attempt" : "No media yet"}</div>;
  if (!video || !src || failed) return <img src={failed || !src ? posterSrc : src} alt={alt} loading="eager" onError={() => failed || !src ? setPosterFailed(true) : setFailed(true)} className={cn(objectFit, "bg-[#151517]", className)} />;
  return <div className={cn("relative overflow-hidden bg-black", className)} data-media-ready={ready ? "true" : "false"}>
    <video src={src} poster={posterSrc} preload="auto" muted playsInline loop controls={controls}
      aria-label={alt || "Creative video"} className={cn("size-full", objectFit)}
      onLoadedMetadata={e => { if (!posterSrc && e.currentTarget.currentTime === 0) e.currentTarget.currentTime = Math.min(0.01, e.currentTarget.duration / 2 || 0.01); }}
      onLoadedData={() => setReady(true)} onSeeked={() => setReady(true)} onError={() => setFailed(true)}
      onMouseEnter={e => { if (!controls && window.matchMedia("(hover: hover) and (prefers-reduced-motion: no-preference)").matches) void e.currentTarget.play().catch(() => {}); }}
      onMouseLeave={e => { if (!controls) e.currentTarget.pause(); }} />
    {posterSrc && !ready && <img src={posterSrc} alt={alt} loading="eager" onError={() => setPosterFailed(true)} className={cn("pointer-events-none absolute inset-0 size-full", objectFit)} />}
  </div>;
}

export function QaBadge({ asset }: { asset: Pick<CreativeAsset, "qaStatus" | "qaReport"> }) {
  const s = asset.qaStatus ?? asset.qaReport?.verdict;
  if (!s || s === "pending") return <Pill tone="muted" size="xs">QA pending</Pill>;
  if (s === "pass")
    return (
      <Pill tone="success" size="xs" icon={<ShieldCheck className="size-3" />}>
        QA pass
      </Pill>
    );
  if (s === "skipped") return <Pill tone="muted" size="xs">QA skipped</Pill>;
  return (
    <Pill tone="danger" size="xs" icon={<CircleAlert className="size-3" />}>
      QA {asset.qaReport?.issues?.length ? `· ${asset.qaReport.issues.length} issue${asset.qaReport.issues.length > 1 ? "s" : ""}` : "fail"}
    </Pill>
  );
}

const ASPECT_CLASS: Record<string, string> = {
  "1:1": "aspect-square",
  "9:16": "aspect-[9/16]",
  "4:5": "aspect-[4/5]",
  "16:9": "aspect-video",
};

/**
 * Every static ships in BOTH 1:1 and 9:16 — show them side by side (missing aspect = dashed slot so
 * the gap is visible). Click to enlarge.
 */
export function AspectPair({ creative, className, height = 220, showQa = true }: { creative: Pick<Creative, "assets" | "kind" | "name">; className?: string; height?: number; showQa?: boolean }) {
  const currentAssets = (creative.assets ?? []).filter((a) => a.status !== "superseded");
  const videos=currentAssets.filter(a=>a.mediaType === "video");
  const poster=currentAssets.find(a=>a.mediaType === "image")?.url;
  const assets = creative.kind === "video" ? videos.length ? videos.map(a=>({...a,thumbnailUrl:a.thumbnailUrl ?? poster})) : currentAssets.map(a=>({...a,mediaType:"video" as const,url:null,thumbnailUrl:a.thumbnailUrl ?? a.url})) : currentAssets;
  const wanted = creative.kind === "video" ? Array.from(new Set(assets.map((a) => a.aspect))) : ["1:1", "9:16"];
  const slots = wanted.length ? wanted : ["1:1", "9:16"];
  const [zoom, setZoom] = useState<CreativeAsset | null>(null);
  return (
    <div className={cn("flex items-end gap-3 overflow-x-auto scrollbar-none", className)}>
      {slots.map((aspect) => {
        const a = assets.find((x) => x.aspect === aspect);
        return (
          <div key={aspect} className="flex shrink-0 flex-col gap-1.5">
            <div className={cn("group relative overflow-hidden rounded-xl border border-line", ASPECT_CLASS[aspect] ?? "aspect-square", !a && "border-dashed")} style={{ height }}>
              {a ? (
                <>
                  <CreativeMedia url={a.url} poster={a.thumbnailUrl} mediaType={a.mediaType} controls={a.mediaType === "video"} className="size-full" alt={`${creative.name} ${aspect}`} />
                  <button
                    type="button"
                    onClick={() => setZoom(a)}
                    aria-label={`Enlarge ${aspect}`}
                    className="absolute right-2 top-2 grid size-7 place-items-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100"
                  >
                    <Maximize2 className="size-3.5" />
                  </button>
                </>
              ) : (
                <div className="grid size-full place-items-center px-2 text-center text-[11px] text-subtle">Missing {aspect}</div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium text-muted">{aspect}</span>
              {a && showQa && <QaBadge asset={a} />}
            </div>
          </div>
        );
      })}
      {zoom && <Lightbox url={zoom.url ?? null} mediaType={zoom.mediaType} caption={`${creative.name} · ${zoom.aspect}`} onClose={() => setZoom(null)} />}
    </div>
  );
}

export function RejectReasonPicker({ value, onChange, className }: { value: RejectionReason[]; onChange: (v: RejectionReason[]) => void; className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)} role="group" aria-label="Rejection reasons">
      {REJECTION_REASONS.map((r) => (
        <Chip key={r} tone="danger" selected={value.includes(r)} onClick={() => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r])}>
          {REJECTION_LABEL[r]}
        </Chip>
      ))}
    </div>
  );
}

/**
 * Approve / Reject-with-reasons. Rejections are training data, so at least one reason is required.
 * Server enforces human-only; the UI only offers it to signed-in humans anyway.
 */
export function ApprovalActions({
  brandId,
  creativeId,
  onDone,
  size = "sm",
  className,
  qaFailed,
}: {
  brandId: string;
  creativeId: string;
  onDone?: (decision: "approved" | "rejected") => void;
  size?: "xs" | "sm" | "md";
  className?: string;
  /** Vision QA failed: approving requires an explicit override (`creatives.approve {overrideQa:true}`). */
  qaFailed?: boolean;
}) {
  const { can, isLoading: authLoading } = useAuth();
  const [rejecting, setRejecting] = useState(false);
  const [reasons, setReasons] = useState<RejectionReason[]>([]);
  const [note, setNote] = useState("");
  const [remember, setRemember] = useState(true);
  const [autoFix, setAutoFix] = useState(true);
  const [picking, setPicking] = useState(false);
  const [references, setReferences] = useState<PickedAsset[]>([]);
  const allowed = can("creative:approve", brandId);
  const approve = useToolMutation("creatives.approve", { success: "Approved — ready to publish (paused)", onSuccess: () => onDone?.("approved") });
  const reject = useToolMutation("creatives.reject", {
    success: autoFix ? "Rejected — a corrected version will be prepared for review" : "Rejected — feedback saved",
    onSuccess: () => {
      setRejecting(false);
      setReasons([]);
      setNote("");
      setReferences([]);
      onDone?.("rejected");
    },
  });
  if (authLoading) {
    return (
      <div className={cn("flex items-center gap-2", className)} aria-busy="true">
        <div className="skeleton h-8 w-20 rounded-lg" />
        <div className="skeleton h-8 w-24 rounded-lg" />
      </div>
    );
  }
  if (!allowed) {
    return <div className={cn("text-[12px] text-muted", className)}>A brand manager needs to approve this.</div>;
  }
  if (rejecting) {
    return (
      <div className={cn("flex flex-col gap-2.5 rounded-xl border border-danger/30 bg-danger-soft/40 p-3", className)}>
        <div className="text-[12.5px] font-medium">What needs to change?</div>
        <RejectReasonPicker value={reasons} onChange={setReasons} />
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain the fix, e.g. “Every ad needs a real app screenshot” or “Match the message bubbles in this reference.”" maxLength={2000} />
        <Checkbox checked={remember} onChange={setRemember} label="Remember reusable feedback for this brand" />
        <Checkbox checked={autoFix} onChange={setAutoFix} label="Prepare a corrected version for review" />
        <div className="flex items-center gap-2"><Button size="xs" variant="ghost" onClick={() => setPicking(true)}>Add visual reference</Button>{references.length > 0 && <span className="text-xs text-muted">{references.length} reference{references.length > 1 ? "s" : ""} <button onClick={() => setReferences([])} className="ml-1 underline">Clear</button></span>}</div>
        <AssetPicker brandId={brandId} open={picking} onClose={() => setPicking(false)} onPick={(items) => setReferences(items.filter((a) => a.mediaType === "image").slice(0, 6))} title="Reference for the correction" />
        <div className="flex justify-end gap-2">
          <Button size={size} variant="ghost" onClick={() => setRejecting(false)}>
            Cancel
          </Button>
          <Button
            size={size}
            variant="danger"
            disabled={reasons.length === 0 || ((remember || autoFix) && !note.trim())}
            loading={reject.isPending}
            onClick={() => reject.mutate({ brandId, creativeId, reasons, note: note.trim() || undefined, rememberForBrand: remember, autoFix, ruleReferenceAssetIds: references.map((a) => a.id) })}
          >
            {autoFix ? "Reject & fix" : "Reject"}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Button size={size} variant="secondary" icon={<X className="size-3.5" />} onClick={() => setRejecting(true)}>
        Reject
      </Button>
      <Button
        size={size}
        variant="primary"
        icon={<Check className="size-3.5" />}
        loading={approve.isPending}
        title={qaFailed ? "Vision QA flagged this creative — approving overrides QA (logged)" : undefined}
        onClick={() => approve.mutate({ brandId, creativeId, ...(qaFailed ? { overrideQa: true } : {}) })}
      >
        {qaFailed ? "Approve anyway" : "Approve"}
      </Button>
    </div>
  );
}

/** Metadata chips: hook · angle · trigger · stage · format. */
export function CreativeMetaChips({ creative, className }: { creative: Pick<Creative, "emotionalTrigger" | "awarenessStage" | "format" | "hookSource">; className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {creative.emotionalTrigger && <Pill tone="primary" size="xs">{humanize(String(creative.emotionalTrigger))}</Pill>}
      {creative.awarenessStage && <Pill tone="info" size="xs">{humanize(String(creative.awarenessStage))}</Pill>}
      {creative.format && <Pill tone="neutral" size="xs">{humanize(String(creative.format))}</Pill>}
      {creative.hookSource && <Pill tone="muted" size="xs">hook: {humanize(String(creative.hookSource))}</Pill>}
    </div>
  );
}
